import { cellCenterLogical, type LogicalPoint } from './coords'
import type {
  GridPosition,
  MapDefinition,
  MapRoute,
  RouteMovementClass
} from './types'

export type WorldPoint = LogicalPoint

export interface PathTrack {
  points: WorldPoint[]
  segmentLengths: number[]
  totalLength: number
}

export type RouteKey = 'ground' | 'air'

/**
 * Canonical route materialized from a map.
 * Unlike the authored MapRoute, movementClass is always resolved.
 */
export interface RouteDefinition {
  id: string
  movementClass: RouteMovementClass
  spawn: GridPosition
  exit: GridPosition
  cells: GridPosition[]
}

export const DEFAULT_GROUND_ROUTE_ID = 'ground-a'
export const DEFAULT_AIR_ROUTE_ID = 'air-a'

/** Authoring ids for the supported ground lanes (1–3 per map). */
export const GROUND_ROUTE_IDS = ['ground-a', 'ground-b', 'ground-c'] as const

/** Movement class of an authored route: explicit field, else id prefix. */
export function routeMovementClass(route: Pick<MapRoute, 'id' | 'movementClass'>): RouteMovementClass {
  if (route.movementClass === 'AIR' || route.movementClass === 'GROUND') {
    return route.movementClass
  }
  return route.id.startsWith('air') ? 'AIR' : 'GROUND'
}

/** Legacy ids stay readable: 'ground' → ground-a, 'air' → air-a. */
export function canonicalRouteId(id: string, movementClass: RouteMovementClass): string {
  const trimmed = id.trim()
  if (trimmed === '') {
    return movementClass === 'AIR' ? DEFAULT_AIR_ROUTE_ID : DEFAULT_GROUND_ROUTE_ID
  }
  if (trimmed === 'ground') return DEFAULT_GROUND_ROUTE_ID
  if (trimmed === 'air') return DEFAULT_AIR_ROUTE_ID
  return trimmed
}

function cloneCells(cells: GridPosition[]): GridPosition[] {
  return cells.map((cell) => ({ x: cell.x, y: cell.y }))
}

function toRouteDefinition(route: MapRoute): RouteDefinition {
  const movementClass = routeMovementClass(route)
  return {
    id: canonicalRouteId(route.id ?? '', movementClass),
    movementClass,
    spawn: { ...route.spawn },
    exit: { ...route.exit },
    cells: cloneCells(route.cells)
  }
}

/** Minimal shape normalizeMapRoutes needs — lets camera helpers reuse it. */
export interface RouteSourceMap {
  spawn: GridPosition
  exit: GridPosition
  path?: GridPosition[]
  routes?: MapRoute[]
  airRoute?: MapRoute | null
}

/**
 * Materialize canonical routes from a map (legacy fields or routes[]).
 *
 * Ground: `routes` entries classified GROUND when present, else the legacy
 * spawn/exit/path lane as `ground-a`.
 * Air: `routes` entries classified AIR when present, else `airRoute` as `air-a`.
 * Ground routes always precede air routes; authored order is kept otherwise.
 */
export function normalizeMapRoutes(map: RouteSourceMap): RouteDefinition[] {
  const listed = (map.routes ?? []).filter((route) => route && route.cells?.length > 0)
  const ground: RouteDefinition[] = []
  const air: RouteDefinition[] = []
  for (const route of listed) {
    const normalized = toRouteDefinition(route)
    const bucket = normalized.movementClass === 'AIR' ? air : ground
    if (bucket.some((entry) => entry.id === normalized.id)) continue
    bucket.push(normalized)
  }

  if (ground.length === 0) {
    const cells = map.path ?? []
    if (cells.length > 0) {
      ground.push({
        id: DEFAULT_GROUND_ROUTE_ID,
        movementClass: 'GROUND',
        spawn: { ...map.spawn },
        exit: { ...map.exit },
        cells: cloneCells(cells)
      })
    }
  }

  if (air.length === 0 && map.airRoute && map.airRoute.cells.length > 0) {
    air.push({
      ...toRouteDefinition(map.airRoute),
      id: canonicalRouteId(map.airRoute.id ?? '', 'AIR'),
      movementClass: 'AIR'
    })
  }

  return [...ground, ...air]
}

/** How many ground lanes the map authors in routes[] (0 for legacy maps). */
export function authoredGroundRouteCount(map: Pick<RouteSourceMap, 'routes'>): number {
  return (map.routes ?? []).filter(
    (route) => route && route.cells?.length > 0 && routeMovementClass(route) === 'GROUND'
  ).length
}

export function routeTrack(route: RouteDefinition): PathTrack {
  return buildPathTrackFromCells(route.cells)
}

/** Lookup by canonical id; legacy 'ground'/'air' aliases resolve too. */
export function findRoute(
  routes: RouteDefinition[],
  id: string
): RouteDefinition | undefined {
  const direct = routes.find((route) => route.id === id)
  if (direct) return direct
  if (id === 'ground') return routes.find((route) => route.id === DEFAULT_GROUND_ROUTE_ID)
  if (id === 'air') return routes.find((route) => route.id === DEFAULT_AIR_ROUTE_ID)
  return undefined
}

/** Canonical lane for a movement class — prefers the *-a id, else first match. */
export function defaultRouteId(
  routes: RouteDefinition[],
  movementClass: RouteMovementClass
): string | null {
  const preferred = movementClass === 'AIR' ? DEFAULT_AIR_ROUTE_ID : DEFAULT_GROUND_ROUTE_ID
  const exact = routes.find(
    (route) => route.movementClass === movementClass && route.id === preferred
  )
  if (exact) return exact.id
  const first = routes.find((route) => route.movementClass === movementClass)
  return first?.id ?? null
}

/**
 * Route completion in 0..1 so enemies on different-length routes compare fairly.
 * Returns 0 for degenerate tracks (zero / non-finite length).
 */
export function normalizedPathProgress(pathProgress: number, totalLength: number): number {
  if (!Number.isFinite(totalLength) || totalLength <= 0) return 0
  if (!Number.isFinite(pathProgress) || pathProgress <= 0) return 0
  return Math.min(1, pathProgress / totalLength)
}

/**
 * Cell-center polyline in logical cell units.
 * Neighbor distance uses Euclidean length (orthogonal = 1).
 * AIR routes may use longer waypoint jumps.
 */
export function buildPathTrackFromCells(cells: GridPosition[]): PathTrack {
  const points = cells.map((cell) => cellCenterLogical(cell.x, cell.y))
  const segmentLengths: number[] = []
  let totalLength = 0
  for (let index = 0; index < points.length - 1; index += 1) {
    const start = points[index]!
    const end = points[index + 1]!
    const length = Math.hypot(end.x - start.x, end.y - start.y)
    segmentLengths.push(length)
    totalLength += length
  }
  return { points, segmentLengths, totalLength }
}

/**
 * Primary ground track (ground-a).
 * Behavior identical to pre-M43 maps, which have exactly one ground lane.
 */
export function buildPathTrack(map: MapDefinition): PathTrack {
  const ground = resolveGroundRoute(map)
  return buildPathTrackFromCells(ground.cells)
}

export function buildAirPathTrack(map: MapDefinition): PathTrack | null {
  const air = resolveAirRoute(map)
  if (!air || air.cells.length === 0) return null
  return buildPathTrackFromCells(air.cells)
}

/** Primary ground lane, reported under the legacy 'ground' id. */
export function resolveGroundRoute(map: MapDefinition): MapRoute {
  const routes = normalizeMapRoutes(map)
  const primary = routes.find((route) => route.movementClass === 'GROUND')
  if (!primary) {
    return {
      id: 'ground',
      spawn: { ...map.spawn },
      exit: { ...map.exit },
      cells: cloneCells(map.path ?? [])
    }
  }
  return {
    id: 'ground',
    spawn: { ...primary.spawn },
    exit: { ...primary.exit },
    cells: cloneCells(primary.cells)
  }
}

/** Primary air lane, reported under the legacy 'air' id (null when absent). */
export function resolveAirRoute(map: MapDefinition): MapRoute | null {
  const air = normalizeMapRoutes(map).find((route) => route.movementClass === 'AIR')
  if (!air) return null
  return {
    id: 'air',
    spawn: { ...air.spawn },
    exit: { ...air.exit },
    cells: cloneCells(air.cells)
  }
}

/** Smooth logical position at a distance along the track. */
export function positionAt(track: PathTrack, distance: number): WorldPoint {
  if (track.points.length === 0) return { x: 0, y: 0 }
  if (distance <= 0 || track.totalLength === 0) return { ...track.points[0]! }
  if (distance >= track.totalLength) return { ...track.points[track.points.length - 1]! }

  let remaining = distance
  for (let index = 0; index < track.segmentLengths.length; index += 1) {
    const length = track.segmentLengths[index]!
    const atEndOfSegment = remaining <= length || index === track.segmentLengths.length - 1
    if (atEndOfSegment) {
      const t = length === 0 ? 0 : remaining / length
      const start = track.points[index]!
      const end = track.points[index + 1]!
      return {
        x: start.x + (end.x - start.x) * t,
        y: start.y + (end.y - start.y) * t
      }
    }
    remaining -= length
  }

  return { ...track.points[track.points.length - 1]! }
}

/** Preferred initial focus — averages every route spawn. */
export function mapPrimaryFocus(map: RouteSourceMap): LogicalPoint {
  const routes = normalizeMapRoutes(map)
  if (routes.length === 0) {
    return { x: map.spawn.x + 0.5, y: map.spawn.y + 0.5 }
  }
  let sumX = 0
  let sumY = 0
  for (const route of routes) {
    sumX += route.spawn.x
    sumY += route.spawn.y
  }
  return {
    x: sumX / routes.length + 0.5,
    y: sumY / routes.length + 0.5
  }
}

/** Route endpoints that framing should keep reachable (all routes). */
export function mapRouteInterestPoints(map: RouteSourceMap): LogicalPoint[] {
  const routes = normalizeMapRoutes(map)
  if (routes.length === 0) {
    return [
      { x: map.spawn.x + 0.5, y: map.spawn.y + 0.5 },
      { x: map.exit.x + 0.5, y: map.exit.y + 0.5 }
    ]
  }
  const points: LogicalPoint[] = []
  for (const route of routes) {
    points.push({ x: route.spawn.x + 0.5, y: route.spawn.y + 0.5 })
    points.push({ x: route.exit.x + 0.5, y: route.exit.y + 0.5 })
  }
  return points
}
